import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Icon, type IconName } from '@/components/ui/icon.js';

interface BottomNavItem {
  to: string;
  labelKey: string;
  icon: IconName;
}

const MAIN_ITEMS: BottomNavItem[] = [
  { to: '/',       labelKey: 'nav.lobby',  icon: 'home' },
  { to: '/casino', labelKey: 'nav.casino', icon: 'play' },
  // Wallet FAB sits between these two and /promos (rendered explicitly below).
  { to: '/promos', labelKey: 'nav.promo',  icon: 'gift' },
] as const;

// The 5th tab — a plain link to Support (account links, including Settings,
// live in the header avatar dropdown). No pop-up: tapping navigates straight to /support.
const CONTACT_ITEM: BottomNavItem = { to: '/support', labelKey: 'nav.contact', icon: 'headset' };

// ponytail: read once at module load — matches the app's other reduced-motion checks; a live
// media-query listener isn't worth it for a nav that mounts for the whole session.
const prefersReducedMotion =
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface MobileBottomNavProps {
  currentPath: string;
}

export function MobileBottomNav({ currentPath }: MobileBottomNavProps) {
  const { t } = useTranslation('common');

  // /wallet owns the center tab.
  const walletActive = currentPath === '/wallet' || currentPath.startsWith('/wallet/');

  // Longest-prefix match so sub-routes keep the parent tab active.
  const isActive = (to: string) =>
    to === '/' ? currentPath === '/' : currentPath === to || currentPath.startsWith(to + '/');

  const renderTab = (item: BottomNavItem) => {
    const active = isActive(item.to);
    return (
      <li key={item.labelKey}>
        <Link
          to={item.to as '/'}
          aria-current={active ? 'page' : undefined}
          className={`relative w-full h-20 flex flex-col items-center justify-center gap-1.5 transition-colors ${
            active ? 'text-[var(--accent-strong)]' : 'text-[var(--text-muted)]'
          }`}
        >
          {active && (
            <motion.span
              layoutId="bf-navpill"
              aria-hidden
              className="absolute top-0 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full bg-[var(--accent-strong)]"
              transition={{ type: 'spring', stiffness: 500, damping: 34 }}
            />
          )}
          <motion.span
            whileTap={{ scale: 0.92 }}
            className={`flex flex-col items-center gap-1.5 transition-transform ${active ? '-translate-y-0.5' : ''}`}
          >
            <Icon name={item.icon} size={23} />
            <span className="text-[11px] font-semibold">{t(item.labelKey)}</span>
          </motion.span>
        </Link>
      </li>
    );
  };

  return (
    <nav
      data-bf-mobile-nav
      className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--border-strong)] bg-[var(--bg-sunken)]/80 backdrop-blur-xl md:hidden"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0px)' }}
    >
      <ul className="grid grid-cols-5">
        {MAIN_ITEMS.slice(0, 2).map(renderTab)}

        {/* Wallet — the money hub, surfaced as the lifted center CTA */}
        <li className="flex items-center justify-center">
          <Link to="/wallet" aria-current={walletActive ? 'page' : undefined} className="-mt-6 flex flex-col items-center justify-center gap-1">
            <motion.span
              whileTap={{ scale: 0.9 }}
              animate={{
                boxShadow: prefersReducedMotion
                  ? walletActive
                    ? '0 0 20px 4px var(--accent-strong)'
                    : '0 0 10px 1px var(--accent-glow)'
                  : walletActive
                    ? ['0 0 12px 2px var(--accent-glow)', '0 0 22px 6px var(--accent-strong)']
                    : ['0 0 6px 1px var(--accent-glow)', '0 0 18px 4px var(--accent-glow)'],
              }}
              transition={
                prefersReducedMotion
                  ? { duration: 0.2 }
                  : { duration: 1.6, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }
              }
              className={`flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent-strong)] text-white ring-4 ring-[var(--bg-sunken)] transition-transform ${
                walletActive ? 'scale-110' : ''
              }`}
            >
              <Icon name="wallet" size={22} />
            </motion.span>
            <span className="text-[10px] font-semibold text-[var(--accent-strong)]">{t('nav.wallet')}</span>
          </Link>
        </li>

        {MAIN_ITEMS.slice(2).map(renderTab)}

        {/* Contact/support — plain navigation (was the Settings tab). */}
        {renderTab(CONTACT_ITEM)}
      </ul>
    </nav>
  );
}
